import { match } from "ts-pattern";
import type { HelpTopicId } from "./topics";

/**
 * What the help panel shows (spec admin-help, "Waar het paneel staat"): open or closed, and the way
 * there: a stack of explanations, the last one shown, for the back button. `null` on the stack is the
 * overview (search and every explanation). Kept in the admin's frame, not in the URL (besluit 12), so
 * it stays when the employee goes to another screen.
 */
export interface HelpState {
	readonly open: boolean;
	readonly stack: readonly (HelpTopicId | null)[];
}

export type HelpAction =
	/** A (?), or "Help" in the menu (`null`): opens it, or closes it when it is what is open. */
	| { readonly type: "toggle"; readonly topic: HelpTopicId | null }
	/** A term, a search result or "Zie ook": shows it, with the way back. */
	| { readonly type: "follow"; readonly topic: HelpTopicId }
	| { readonly type: "back" }
	| { readonly type: "close" };

export const closedHelp: HelpState = { open: false, stack: [] };

/** What is shown: an explanation, the overview (`null`), or nothing when closed. */
export const shownTopic = (state: HelpState): HelpTopicId | null | undefined =>
	state.open ? state.stack.at(-1) : undefined;

export function helpReducer(state: HelpState, action: HelpAction): HelpState {
	return (
		match(action)
			// A second click on the (?) of what is shown closes; any other (?) replaces it.
			.with({ type: "toggle" }, ({ topic }) =>
				shownTopic(state) === topic ? closedHelp : { open: true, stack: [topic] },
			)
			.with({ type: "follow" }, ({ topic }) => ({
				open: true,
				stack: state.open ? [...state.stack, topic] : [topic],
			}))
			.with({ type: "back" }, () =>
				state.stack.length > 1 ? { ...state, stack: state.stack.slice(0, -1) } : state,
			)
			.with({ type: "close" }, () => closedHelp)
			.exhaustive()
	);
}
